import React, { useEffect, useMemo, useState } from "react";
import { generatePath, useHistory, useParams } from "react-router-dom";
import { MultiTabView } from "../../common/multitab/MultiTabView";
import { TabInfo } from "../../common/multitab/TabInfo";
import { NEW_STORE_ROUTE_PATH } from "../../navigation/Routes";
import { NavigationHelper } from "../../navigation/PrevLocationState";
import { StoreCreateDetailsTab } from "./StoreCreateDetailsTab";
import { StoreCreateThemesTab } from "./StoreCreateThemesTab";
import { StoreCreateProvider, useStoreCreateContext } from "./StoreCreateContext";
import { CenteredSpinner } from "../../common/CenteredSpinner";
import { EmptyState } from "../../common/EmptyState";
import { ErrorIcon } from "evergreen-ui";

type Params = { tabId?: StoreTabIds };

export enum StoreTabIds {
    details = "details",
    themes = "themes",
}

const resolveSelectedTabIndex = (tabId?: StoreTabIds): number => {
    switch (tabId) {
        case StoreTabIds.themes:
            return 1;
        case StoreTabIds.details:
        default:
            return 0;
    }
};

const MultiTabNewStorePageContent = () => {
    const history = useHistory();

    const { tabId } = useParams<Params>();

    const [
        selectedTabIndex,
        setSelectedTabIndex,
    ] = useState<number>(tabId ? resolveSelectedTabIndex(tabId) : resolveSelectedTabIndex(StoreTabIds.details));

    const {
        formValues,
        storefrontsData,
        storefrontsLoading,
        storefrontsError,
    } = useStoreCreateContext();

    const tabs: TabInfo[] = useMemo(() => ([
        {
            label: "Details",
            tabId: "details",
            component: <StoreCreateDetailsTab />,
        },
        {
            label: "Themes",
            tabId: "themes",
            component: <StoreCreateThemesTab />,
        },
    ]), []);

    const headerText: string = useMemo(() => {
        return !!formValues.storeName.trim() ? formValues.storeName : "New Store";
    }, [formValues.storeName]);

    useEffect(() => {
        NavigationHelper.pushPathname(
            history,
            generatePath(NEW_STORE_ROUTE_PATH, {
                tabId: tabs[selectedTabIndex].tabId,
            }),
        );
    }, [selectedTabIndex, history, tabs]);

    if (!storefrontsLoading && storefrontsError) {
        return (
            <EmptyState
                title={"Error loading storefront data"}
            >
                <ErrorIcon size={60} />
            </EmptyState>
        );
    }

    return storefrontsData ? (
        <MultiTabView
            header={headerText}
            tabs={tabs}
            selectedTabIndex={selectedTabIndex}
            onTabSelected={setSelectedTabIndex}
            fullPathFormat={NEW_STORE_ROUTE_PATH}
            backButtonHeader="All Stores"
            backButtonOnClick={() => { history.push("/stores"); }}
        />
    ) : (
        <CenteredSpinner />
    );
};

export const MultiTabNewStorePage = () => {
    return (
        <StoreCreateProvider>
            <MultiTabNewStorePageContent />
        </StoreCreateProvider>
    );
};
